/**
 * QL-375 — the allocation rules every mandate's lines obey, whichever side
 * checks them: the API refusing a template or a bank mandate, the web dialog
 * disabling Save with a reason. Pure; one tolerance for both.
 *
 *   - fixed:   0 ≤ weight ≤ 100 per line, Σ weight = 100
 *   - min_max: 0 ≤ min ≤ default ≤ max ≤ 100 per line, Σ default = 100, and the
 *              bands must permit an allocation totalling 100 (Σ min ≤ 100 ≤ Σ max)
 */

import type { MandateTemplateMode } from "./types/mandate";

/** Sums of 4-dp percentages drift in floating point; this is the slack allowed. */
export const MANDATE_PCT_TOLERANCE = 1e-6;

export type MandateLineRuleCode =
  | "mode_fields"
  | "pct_range"
  | "fixed_total"
  | "range_order"
  | "default_total"
  | "range_unreachable";

export type MandateLineRuleFigures = {
  weightPct?: number | null;
  minPct?: number | null;
  defaultPct?: number | null;
  maxPct?: number | null;
};

export type MandateLineRuleProblem = {
  code: MandateLineRuleCode;
  /** The figure the rule judged, for the message: a total, or the two band sums. */
  total?: number;
  mins?: number;
  maxs?: number;
};

function isPct(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= 100;
}

function sum(values: number[]): number {
  return values.reduce((acc, v) => acc + v, 0);
}

/**
 * The first broken figure rule for the mode, in the order a designer would fix
 * them — shape, per-line ranges, then the totals — or null when the lines are
 * sound. Instrument identity (one line per instrument) is the caller's.
 */
export function mandateLineRuleProblem(
  mode: MandateTemplateMode,
  lines: readonly MandateLineRuleFigures[],
): MandateLineRuleProblem | null {
  if (mode === "fixed") {
    for (const line of lines) {
      if (line.minPct != null || line.defaultPct != null || line.maxPct != null) {
        return { code: "mode_fields" };
      }
      if (!isPct(line.weightPct)) return { code: "pct_range" };
    }
    const total = sum(lines.map((l) => l.weightPct as number));
    if (Math.abs(total - 100) > MANDATE_PCT_TOLERANCE) return { code: "fixed_total", total };
    return null;
  }
  for (const line of lines) {
    if (line.weightPct != null) return { code: "mode_fields" };
    if (!isPct(line.minPct) || !isPct(line.defaultPct) || !isPct(line.maxPct)) {
      return { code: "pct_range" };
    }
    if (!(line.minPct <= line.defaultPct && line.defaultPct <= line.maxPct)) {
      return { code: "range_order" };
    }
  }
  const defaults = sum(lines.map((l) => l.defaultPct as number));
  if (Math.abs(defaults - 100) > MANDATE_PCT_TOLERANCE) {
    return { code: "default_total", total: defaults };
  }
  // Implied by the two rules above; kept as a defensive guard.
  const mins = sum(lines.map((l) => l.minPct as number));
  const maxs = sum(lines.map((l) => l.maxPct as number));
  if (mins - MANDATE_PCT_TOLERANCE > 100 || maxs + MANDATE_PCT_TOLERANCE < 100) {
    return { code: "range_unreachable", mins, maxs };
  }
  return null;
}
